import { FC, useEffect, useState } from 'react';

import { CurrencyCodeType } from '@mint-lib/currencies-helpers';
import { Trans, useTranslation } from '@mint-lib/i18n';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  LoadingButton,
  SelectWithSearch,
  Typography,
} from '@myn/mui';

import {
  useGetCurrenciesQuery,
  useLazyGetCurrencyRateQuery,
} from '../../../../api/currenciesApi.js';
import { RowToChangeCurrencyObject } from '../../types.js';
import { prepareOptionList } from './utils.js';

export type RowCurrencyModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (currency: CurrencyCodeType | null) => void;
  rowData: RowToChangeCurrencyObject | null;
  isRowCurrencySending: boolean;
};
const ChangeRowCurrencyModal: FC<RowCurrencyModalProps> = ({
  open,
  onClose,
  onSubmit,
  rowData,
  isRowCurrencySending,
}) => {
  const { t } = useTranslation('@myn/planning');

  const [selectedOption, setSelectedOption] = useState<CurrencyCodeType | null>(
    null,
  );
  const [rate, setRate] = useState<number | null>(null);
  const [isCurrencyErrorModalShown, setCurrencyErrorModalShown] =
    useState<boolean>(false);

  const {
    isFetching: isCurrencyListLoading,
    isError: isCurrencyListError,
    options,
    refetch: refetchCurrencyList,
  } = useGetCurrenciesQuery(undefined, {
    skip: !open,
    selectFromResult: (result) => {
      return {
        ...result,
        options: prepareOptionList(result.data?.data.items),
      };
    },
  });

  const [getCurrencyRate, { isFetching: isCurrencyRateLoading }] =
    useLazyGetCurrencyRateQuery();

  const showLoader =
    isCurrencyListLoading && !isCurrencyListError && !options.length;
  const showError =
    isCurrencyListError && !isCurrencyListLoading && !options.length;
  const showSelectWithOptions = !isCurrencyListLoading && !isCurrencyListError;
  const shouldUpdateSelectedOption =
    open && !!rowData?.rowCurrency && !selectedOption;

  useEffect(() => {
    if (shouldUpdateSelectedOption) {
      setSelectedOption(rowData.rowCurrency);
    }

    return () => {
      setSelectedOption(null);
      setRate(null);
    };
  }, [open]);

  const handleOptionChange = (option: CurrencyCodeType | '') => {
    setSelectedOption(option || null);
  };

  const handleSubmit = async () => {
    if (!rowData?.rowCurrency || !selectedOption) {
      return onSubmit(null);
    }

    try {
      const request = await getCurrencyRate({
        dateFrom: rowData.startDate,
        dateTo: rowData.endDate,
        fromCurrency: rowData.rowCurrency,
        toCurrency: selectedOption,
      }).unwrap();

      if (request.type === 'api') {
        setRate(request.rate);
        return;
      }
    } catch (e) {
      setCurrencyErrorModalShown(true);
      return;
    }

    onSubmit(selectedOption);
  };

  const handleCurrencyMismatchModalClose = () => {
    setRate(null);
  };

  const handleCurrencyMismatchModalSubmit = () => {
    onSubmit(selectedOption);
    handleCurrencyMismatchModalClose();
  };

  const handleCurrencyErrorModalClose = () => {
    setCurrencyErrorModalShown(false);
    setRate(null);
    onClose();
  };

  const handleCurrencyErrorModalSubmit = () => {
    setCurrencyErrorModalShown(false);
  };

  return (
    <>
      {/* main change row currency modal */}
      <Dialog
        size="xs"
        title={t('Set media row currency')}
        open={open}
        onClose={onClose}
        submitButton={
          <LoadingButton
            color="primary"
            size="large"
            variant="contained"
            onClick={handleSubmit}
            loading={isRowCurrencySending || isCurrencyRateLoading}
            disabled={
              rowData?.rowCurrency === selectedOption || !options.length
            }
          >
            {t('Save')}
          </LoadingButton>
        }
        cancelButtonProps={{
          label: t('Cancel'),
          color: 'primary',
          size: 'large',
          variant: 'text',
          onClick: onClose,
        }}
      >
        {showLoader && (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', height: '40px' }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        {showError && (
          <Alert
            title={t('Error')}
            subTitle={t('Something went wrong')}
            actions={
              <Button
                color="error"
                variant="outlined"
                size="small"
                onClick={refetchCurrencyList}
              >
                {t('Try again')}
              </Button>
            }
            severity="error"
            textLayout="inline"
          />
        )}
        {showSelectWithOptions && (
          <SelectWithSearch
            fullWidth
            value={selectedOption || ''}
            options={options}
            onChange={handleOptionChange}
            placeholder="€ (EUR)"
          />
        )}
      </Dialog>

      {/* currency mismatch modal (it appears if manual currency rate is absent) */}
      <Dialog
        size="m"
        title={t('Currency Mismatch')}
        open={!!rate}
        onClose={handleCurrencyMismatchModalClose}
        submitButton={
          <LoadingButton
            color="primary"
            size="large"
            variant="contained"
            onClick={handleCurrencyMismatchModalSubmit}
            loading={isRowCurrencySending}
          >
            {t('Yes, confirm')}
          </LoadingButton>
        }
        cancelButtonProps={{
          label: t('Select another currency'),
          color: 'primary',
          size: 'large',
          variant: 'text',
          onClick: handleCurrencyMismatchModalClose,
        }}
      >
        <Typography variant="bodyLong01" component="p">
          <Trans
            i18nKey="Would you like to convert your <Bold>{{rowName}}</Bold> from <Bold>{{currentCurrency}}</Bold> currency to <Bold>{{newCurrency}}</Bold> currency?"
            t={t}
            values={{
              rowName: rowData?.rowName,
              currentCurrency: rowData?.rowCurrency,
              newCurrency: selectedOption,
            }}
            components={{
              Bold: <Typography variant="bodyLong02" />,
            }}
          />
        </Typography>
        <Typography variant="bodyLong01" component="p">
          <Trans
            i18nKey="Please be aware that there is no established exchange rate for the <Bold>{{currentCurrency}}</Bold> currency, and it will be obtained from global sources via the API."
            t={t}
            values={{
              currentCurrency: rowData?.rowCurrency,
            }}
            components={{
              Bold: <Typography variant="bodyLong02" />,
            }}
          />
        </Typography>
        <Typography variant="bodyLong01" component="p">
          <Trans
            i18nKey="We will apply the exchange rate <Bold>1 {{currentCurrency}} = {{rate}} {{newCurrency}}</Bold>."
            t={t}
            values={{
              currentCurrency: rowData?.rowCurrency,
              newCurrency: selectedOption,
              rate,
            }}
            components={{
              Bold: <Typography variant="bodyLong02" />,
            }}
          />
        </Typography>
      </Dialog>

      {/* currency error modal (it appears if it isn't possible to get the currency rate) */}
      <Dialog
        size="m"
        title={t('Currency issue')}
        open={isCurrencyErrorModalShown}
        onClose={handleCurrencyErrorModalClose}
        submitButtonProps={{
          label: t('Select another currency'),
          color: 'warning',
          size: 'large',
          variant: 'contained',
          onClick: handleCurrencyErrorModalSubmit,
        }}
        cancelButtonProps={{
          label: t('Cancel'),
          color: 'primary',
          size: 'large',
          variant: 'text',
          onClick: handleCurrencyErrorModalClose,
        }}
      >
        <Typography variant="bodyLong01" component="p">
          {t(
            'An issue occurred while attempting to retrieve the exchange rate from global sources via the API.',
          )}
        </Typography>
        <Typography variant="bodyLong01" component="p">
          {t('Please try again, or consider using an alternative currency')}
        </Typography>
      </Dialog>
    </>
  );
};

export default ChangeRowCurrencyModal;
